import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import PageContainer from '../PageContainer/PageContainer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import { FooterRatingStars } from '../Footer/FooterIcons';
import '../Footer/footerIcons.css';
import '../shared/ordersPageShell.css';
import '../MyAccount/MyAccount.css';
import OrderFeedbackModal from '../OrderFeedback/OrderFeedbackModal';
import CustomerReviewList from './CustomerReviewList';
import restaurantService, {
  Restaurant,
  RestaurantCustomerReview,
  RestaurantCustomerReviewSummary,
  UserOrderDetails,
} from '../../services/restaurantService';
import { useAppSelector } from '../../store/hooks';
import { getRestaurantDisplayRating } from '../../utils/restaurantRating';
import { isCompletedOrderStatus } from '../../utils/orderStatus';
import { REVIEWS_PAGE_SIZE } from '../../constants/customerReviews';
import { useSsrPreload, matchSsrPreload } from '../../ssr/preload';
import SeoHead from '../Seo/SeoHead';
import { getRestaurantImageUrl } from '../../constants/images';
import './RestaurantReviews.css';

const RestaurantReviews: React.FC = () => {
  const { appName } = useParams<{ appName: string }>();
  const { isAuthenticated } = useAppSelector((state) => state.user);
  const preload = useSsrPreload();
  const reviewsPreload = matchSsrPreload(preload, 'reviews', appName) ? preload : null;

  const [restaurant, setRestaurant] = useState<Restaurant | null>(reviewsPreload?.restaurant ?? null);
  const [reviews, setReviews] = useState<RestaurantCustomerReview[]>(reviewsPreload?.customerReviews ?? []);
  const [summary, setSummary] = useState<RestaurantCustomerReviewSummary | null>(
    reviewsPreload?.customerReviewSummary ?? null
  );
  const [loading, setLoading] = useState(!reviewsPreload?.restaurant);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [rateableOrders, setRateableOrders] = useState<UserOrderDetails[]>([]);
  const [feedbackModalOrderId, setFeedbackModalOrderId] = useState<number | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const loadReviews = useCallback(
    async (offset: number, append: boolean) => {
      if (!appName) return;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const [restRes, reviewsRes] = await Promise.all([
          offset === 0 ? restaurantService.getRestaurant(appName) : Promise.resolve(null),
          restaurantService.getRestaurantReviews(appName, REVIEWS_PAGE_SIZE, offset),
        ]);

        if (offset === 0 && restRes?.success && restRes.restaurant) {
          setRestaurant(restRes.restaurant);
        }

        if (reviewsRes.success) {
          const nextReviews = reviewsRes.reviews || [];
          setReviews((prev) => (append ? [...prev, ...nextReviews] : nextReviews));
          if (reviewsRes.summary) {
            setSummary(reviewsRes.summary);
          }
          const total = reviewsRes.summary?.reviewCount ?? nextReviews.length;
          setHasMore(offset + nextReviews.length < total);
        } else {
          setError(reviewsRes.message || 'Failed to load reviews');
        }
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        setError(message || 'Failed to load reviews');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [appName]
  );

  const loadRateableOrders = useCallback(async () => {
    if (!appName || !isAuthenticated) {
      setRateableOrders([]);
      return;
    }
    try {
      const response = await restaurantService.getMyOrders(appName, 'history');
      if (!response.success || !response.orders) {
        setRateableOrders([]);
        return;
      }
      const eligible = response.orders.filter((order) => {
        const { userOrder, feedbackSummary } = order;
        if (!isCompletedOrderStatus(userOrder.orderStatus) || !feedbackSummary) {
          return false;
        }
        return feedbackSummary.eligible || feedbackSummary.canEdit;
      });
      setRateableOrders(eligible);
    } catch {
      setRateableOrders([]);
    }
  }, [appName, isAuthenticated]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
    if (reviewsPreload?.restaurant && reviewsPreload.appName === appName) {
      void loadRateableOrders();
      return;
    }
    void loadReviews(0, false);
    void loadRateableOrders();
  }, [loadReviews, loadRateableOrders]);

  const pepparAverageRating = summary?.averageRating ?? restaurant?.pepparRating ?? null;
  const pepparReviewCount = summary?.reviewCount ?? reviews.length;
  const displayListingRating = restaurant ? getRestaurantDisplayRating(restaurant) : null;

  const breadcrumbItems = useMemo(
    () => [
      {
        label: restaurant?.restaurantName || 'Restaurant',
        path: appName ? `/restaurants/${appName}` : undefined,
      },
      ...(appName ? [{ label: 'Menu', path: `/restaurants/${appName}/menu` }] : []),
      { label: 'Reviews', active: true },
    ],
    [appName, restaurant?.restaurantName]
  );

  const formatOrderDate = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleFeedbackSubmitted = async () => {
    setFeedbackModalOrderId(null);
    setToastMessage('Thank you for your feedback!');
    await Promise.all([loadReviews(0, false), loadRateableOrders()]);
  };

  return (
    <div className="my-account-page rr-page">
      <SeoHead
        title={restaurant ? `${restaurant.restaurantName} reviews` : 'Reviews'}
        description={
          restaurant
            ? `Guest reviews for ${restaurant.restaurantName}.`
            : 'Restaurant reviews'
        }
        path={appName ? `/restaurants/${appName}/reviews` : undefined}
        images={
          restaurant?.coverImage
            ? [getRestaurantImageUrl(restaurant.id, restaurant.coverImage)]
            : undefined
        }
      />
      <Header />
      <PageContainer>
        <div className="orders-page-shell rr-shell">
          <Breadcrumb items={breadcrumbItems} />

          {loading && !restaurant ? (
            <div className="rr-loading">
              <i className="fa fa-spinner fa-spin" aria-hidden /> Loading reviews...
            </div>
          ) : error && reviews.length === 0 ? (
            <div className="rr-error">{error}</div>
          ) : (
            <>
              <div className="account-page-header">
                <h1 className="account-page-title">Customer Reviews</h1>
              </div>

              <section className="my-account-card rr-card">
                {pepparAverageRating != null && pepparAverageRating > 0 ? (
                  <div className="cr-summary">
                    <FooterRatingStars rating={pepparAverageRating} className="cr-summary-stars" />
                    <span className="cr-summary-val">{pepparAverageRating.toFixed(1)}</span>
                    <span className="cr-summary-max">/ 5</span>
                    {pepparReviewCount > 0 && (
                      <span className="cr-summary-count">
                        Based on {pepparReviewCount} review{pepparReviewCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    {displayListingRating != null &&
                      displayListingRating > pepparAverageRating && (
                        <span className="cr-summary-note">
                          Best overall {displayListingRating.toFixed(1)} including platform ratings
                        </span>
                      )}
                  </div>
                ) : (
                  <p className="cr-empty">No customer reviews yet. Be the first to share your experience.</p>
                )}

                {!isAuthenticated && (
                  <div className="rr-rate-banner">
                    <p>Sign in and complete an order to leave a verified review.</p>
                    <Link to="/signin" className="rr-rate-link">
                      Sign in
                    </Link>
                  </div>
                )}

                {isAuthenticated && rateableOrders.length > 0 && (
                  <div className="rr-rate-section">
                    <h2 className="rr-rate-title">Rate your recent visit</h2>
                    <p className="rr-rate-desc">
                      Only verified customers who completed an order can submit a review.
                    </p>
                    <ul className="rr-rate-orders">
                      {rateableOrders.map((order) => {
                        const { userOrder, feedbackSummary } = order;
                        const orderCode = userOrder.orderCode || String(order.orderId);
                        const canEdit = feedbackSummary?.canEdit;
                        return (
                          <li key={order.orderId} className="rr-rate-order">
                            <div>
                              <div className="rr-rate-order-code">Order #{orderCode}</div>
                              <div className="rr-rate-order-date">
                                {formatOrderDate(userOrder.createdOn)}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="rr-rate-btn"
                              onClick={() => setFeedbackModalOrderId(order.orderId)}
                            >
                              {canEdit ? 'Edit review' : 'Leave a review'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {isAuthenticated && rateableOrders.length === 0 && (
                  <div className="rr-rate-banner rr-rate-banner--muted">
                    <p>
                      You have no orders eligible for review right now. Complete a visit and check back
                      within 7 days, or browse{' '}
                      <Link to={appName ? `/restaurants/${appName}/my-orders` : '/my-orders'}>
                        Your Orders
                      </Link>
                      .
                    </p>
                  </div>
                )}

                <CustomerReviewList reviews={reviews} />

                {hasMore && (
                  <div className="rr-load-more-wrap">
                    <button
                      type="button"
                      className="rr-load-more-btn"
                      disabled={loadingMore}
                      onClick={() => void loadReviews(reviews.length, true)}
                    >
                      {loadingMore ? 'Loading...' : 'Load more reviews'}
                    </button>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </PageContainer>
      <Footer />

      {appName && feedbackModalOrderId != null && (
        <OrderFeedbackModal
          appName={appName}
          orderId={feedbackModalOrderId}
          open
          onClose={() => setFeedbackModalOrderId(null)}
          onSubmitted={() => void handleFeedbackSubmitted()}
          onNotify={(message, type) => {
            if (type === 'error') setToastMessage(message);
          }}
        />
      )}

      {toastMessage && (
        <div className="rr-toast" role="status">
          {toastMessage}
          <button type="button" onClick={() => setToastMessage(null)} aria-label="Dismiss">
            <i className="fa fa-times" aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
};

export default RestaurantReviews;
